import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { getCargoSequencingLockReason } from './stowageRotationUtils';

export const sequenceValue = (operation: RotationCargoOperation): number =>
    Number.isSafeInteger(operation.cargoOperationSequenceNumber) && operation.cargoOperationSequenceNumber > 0
        ? operation.cargoOperationSequenceNumber
        : Number.POSITIVE_INFINITY;

export const sortOperationsBySequence = (operations: RotationCargoOperation[]): RotationCargoOperation[] =>
    [...operations].sort((a, b) => sequenceValue(a) - sequenceValue(b));

export type SequenceDropPosition = 'before' | 'after';

export const reorderCargoSequence = (
    plan: StowagePlan,
    berth: RotationBerthCall,
    sourceGuid: string,
    targetGuid: string,
    position?: SequenceDropPosition
) => {
    if (berth.berthId < 0) throw new Error('Cargo sequencing is not available for transit.');
    const lockReason = getCargoSequencingLockReason(berth.portCall, plan.rotation);
    if (lockReason) throw new Error(lockReason);
    const operations = sortOperationsBySequence(berth.cargoOperations.filter((operation) => operation.operationType !== 'Transit'));
    const originalPositions = new Map(operations.map((operation, index) => [operation.cargoOperationGuid, index]));
    const sourceIndex = operations.findIndex((operation) => operation.cargoOperationGuid === sourceGuid);
    const targetIndex = operations.findIndex((operation) => operation.cargoOperationGuid === targetGuid);
    if (sourceIndex < 0 || targetIndex < 0) throw new Error('The sequence operation is no longer in this berth. Reload and try again.');
    if (sourceIndex === targetIndex) return [];
    const insertionIndex = position ? targetIndex + (position === 'after' ? 1 : 0) - (sourceIndex < targetIndex ? 1 : 0) : targetIndex;
    if (insertionIndex === sourceIndex) return [];
    const [source] = operations.splice(sourceIndex, 1);
    operations.splice(insertionIndex, 0, source);
    const changes = operations.flatMap((operation, index) => {
        const nextSequence = index + 1;
        const eventOrderOverride = { berthCallGuid: berth.berthCallGuid, index: berth.cargoOperations.indexOf(operation) };
        const orderChanged =
            operation.eventOrderOverride?.berthCallGuid !== eventOrderOverride.berthCallGuid ||
            operation.eventOrderOverride?.index !== eventOrderOverride.index;
        if (operation.cargoOperationSequenceNumber === nextSequence && !orderChanged) return [];
        const original = operation.hasChangedSequence ?? {
            originalSequenceNumber: operation.cargoOperationSequenceNumber,
            originalIndexInBerthCall: originalPositions.get(operation.cargoOperationGuid),
            originalParentGuid: berth.berthCallGuid,
        };
        operation.hasChangedSequence =
            nextSequence === original.originalSequenceNumber && original.originalParentGuid === berth.berthCallGuid ? null : original;
        operation.cargoOperationSequenceNumber = nextSequence;
        operation.eventOrderOverride = eventOrderOverride;
        return [
            createStowagePlanChange(plan.stowagePlanGuid, operation.cargoOperationGuid, StowageChangeDataType.RotationCargoOperation, {
                cargoOperationSequenceNumber: nextSequence,
                hasChangedSequence: operation.hasChangedSequence,
                eventOrderOverride,
            }),
        ];
    });
    if (changes.length && plan.manifest) delete plan.manifest.cargoSequenceSnapshots;
    return changes;
};
